import React, { useEffect, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { CustomButton, TextField } from "../../../../v2/MUI_custom_component";
import { toCapitalized } from "../../../../v2/Utils/Capitalize";
import { onboardThroughExistingUser } from "../../API/Action";
import { searchCompanyAction } from "../../../../v2/APIs/action";
import AddressForm from "./AddressForm";
import UserDetailsPreview from "./UserDetailsPreview";
import {
  emptyAddress,
  toApiDob,
  isNonEmptyAddress,
} from "../../utils/addressMapper";
import {
  backToUserDetails,
  backToAadhaar,
  backToGstInput,
  backToRole,
} from "../../utils/backNavigation";

const UserAddressStep = ({
  onPreviewChange,
  onRequestCancel,
  onBackState,
  requestBackWith,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const origin = useLocation().state?.origin;
  const { kycDetails, userDetails, selectedRole, manufacturer, kycMethod } =
    useSelector((state) => state.onboardBuyerReducer);
  const selectedSupplier = useSelector(
    (state) => state.entityReducer.selectedSupplier,
  );
  const vfcToken = useSelector((state) => state.authReducer.VFC_TOKEN);

  const roleName = selectedRole?.display_name
    ? toCapitalized(selectedRole.display_name)
    : "";

  const isVcp = !!(kycDetails?.company_gstin || kycDetails?.company_name);

  const [operational, setOperational] = useState(
    () => kycDetails?.operational_address || emptyAddress(),
  );
  const [errors, setErrors] = useState({});

  const seededRef = useRef(!!kycDetails?.operational_address);
  useEffect(() => {
    if (!seededRef.current && kycDetails?.operational_address) {
      seededRef.current = true;
      setOperational(kycDetails.operational_address);
    }
  }, [kycDetails]);

  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    onPreviewChange?.(showPreview);
    return () => onPreviewChange?.(false);
  }, [showPreview, onPreviewChange]);

  const addressBack = () => {
    if (kycMethod === "AADHAAR_OCR") return backToAadhaar(dispatch, "upload");
    if (kycMethod === "AADHAAR_MANUAL") return backToAadhaar(dispatch, "manual");
    if (kycMethod === "GST") return backToGstInput(dispatch);
    return backToUserDetails(dispatch); // DigiLocker / default
  };
  const previewBack = () => {
    if (kycMethod === "GST") return backToGstInput(dispatch);
    return backToUserDetails(dispatch);
  };


  useEffect(() => {
    onBackState?.({
      screen: showPreview ? "preview" : "address",
      hasData: isNonEmptyAddress(operational),
      back: showPreview ? previewBack : addressBack,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showPreview, kycMethod, operational]);

  const validate = () => {
    const company = operational.company_address || {};
    const next = {};
    if (!company.address_line_1?.trim())
      next.address_line_1 = "Address line 1 is required";
    if (!company.address_line_2?.trim())
      next.address_line_2 = "Address line 2 is required";
    if (!operational.state_id) next.state_id = "State is required";
    if (!operational.district_id) next.district_id = "District is required";
    if (!operational.location_id) next.location_id = "City is required";
    if (!operational.pincode) next.pincode = "Pincode is required";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const submitOnboard = async (operationalAddress) => {
    const verified = !!kycDetails?.verified;
    const regAddr = kycDetails?.registered_address;
    const opAddr = operationalAddress || {};
    const isEmptyVal = (v) => v === null || v === undefined || v === "";
    const registeredAddress = isNonEmptyAddress(regAddr)
      ? {
          ...regAddr,
          state_id: isEmptyVal(regAddr.state_id)
            ? opAddr.state_id
            : regAddr.state_id,
          district_id: isEmptyVal(regAddr.district_id)
            ? opAddr.district_id
            : regAddr.district_id,
          pincode: isEmptyVal(regAddr.pincode)
            ? opAddr.pincode
            : regAddr.pincode,
        }
      : operationalAddress;

    const payload = {
      firstname: kycDetails?.firstname || "",
      lastname: kycDetails?.lastname || "",
      email: null,
      phone_number: userDetails?.mobileNumber,
      company_name: kycDetails?.company_name || null,
      company_gstin: kycDetails?.company_gstin || null,
      pancard_no: kycDetails?.pancard_no || null,
      operational_address: operationalAddress,
      registered_address: registeredAddress,
      dob: toApiDob(kycDetails?.date_of_birth),
      manufacturer_id:
        manufacturer?.manufacturer_id ?? Number(selectedSupplier),
      firm_role_id: selectedRole?.firm_role_id,
      vfc_token: vfcToken,
      verified,
      pricing_geography_id: kycDetails?.pricing_geography_id ?? null,
      privacy_policy_accept_status: true,
      ...(verified ? { verification_data: kycDetails?.verification_data } : {}),
    };

    setSubmitting(true);
    const res = await onboardThroughExistingUser(payload);
    setSubmitting(false);

    if (res?.error) {
      toast.error(res.message || "Failed to onboard");
      return;
    }

    const addr = operationalAddress || {};
    const company = addr.company_address || {};
    let billInfo = {
      company_name: kycDetails?.company_name || null,
      company_id: res?.data?.company_id ?? null,
      company_phone_no: userDetails?.mobileNumber,
      user_esi_id: null,
      company_address1: company.address_line_1 || "",
      company_address2: company.address_line_2 || "",
      company_address3: company.address_line_3 || "",
      company_pincode: addr.pincode ?? null,
      district_id: addr.district_id ?? null,
      location_id: addr.location_id ?? null,
      location_name: addr.location_name || "",
      state_id: addr.state_id ?? null,
      pricing_geography_id: kycDetails?.pricing_geography_id ?? null,
      pancard_no: kycDetails?.pancard_no || null,
      gstin: kycDetails?.company_gstin || null,
    };

    if (origin) {
      try {
        const searchRes = await searchCompanyAction({
          supplier_id: Number(
            manufacturer?.manufacturer_id ?? selectedSupplier,
          ),
          firm_role_ids: [selectedRole?.firm_role_id].filter(
            (v) => v != null,
          ),
          company_phone: userDetails?.mobileNumber,
        });
        const buyer = searchRes?.data?.[0];
        const gi = buyer?.geographic_info;
        if (gi) {
          billInfo = {
            company_name: buyer.name,
            company_id: buyer.id,
            company_phone_no: buyer.phone_no,
            user_esi_id:
              buyer.esi_info_with_operational_address?.[0]?.esi_info?.esi_id ??
              null,
            company_address1: gi.address_info?.address_line_1 || "",
            company_address2: gi.address_info?.address_line_2 || "",
            company_address3: gi.address_info?.address_line_3 || "",
            company_pincode: gi.pincode_info?.pincode_id ?? null,
            district_id: gi.district_info?.district_id ?? null,
            location_id: gi.location_info?.location_id ?? null,
            location_name: gi.location_info?.location_name || "",
            pricing_geography_id:
              gi.pricing_geography_info?.pricing_geography_id ?? null,
            pricing_geography_name:
              gi.pricing_geography_info?.pricing_geography_name || "",
            state_id: gi.state_info?.state_id ?? null,
            pancard_no:
              buyer.gstin_pan && buyer.gstin_pan.length === 10
                ? buyer.gstin_pan
                : null,
            gstin:
              buyer.gstin_pan && buyer.gstin_pan.length === 15
                ? buyer.gstin_pan
                : null,
            esi_info_with_operational_address:
              buyer.esi_info_with_operational_address,
          };
        }
      } catch (e) {
        // keep the operational-address fallback
      }
    }

    navigate("/onboard-buyer-success", {
      state: {
        role: roleName,
        isInfluencer: selectedRole?.region_role_id == null,
        isManualKyc: kycMethod === "AADHAAR_MANUAL",
        isSupplierApprovalRequired:
          !!selectedRole?.onboarding_config?.is_supplier_approval_required,
        message: res?.message,
        origin,
        billInfo,
      },
    });
  };

  const handleAddressContinue = () => {
    if (!validate()) return;

    dispatch({
      type: "UPDATE_KYC_DETAILS",
      payload: { operational_address: operational },
    });

    setPreviewData({
      role: roleName,
      gender: kycDetails?.gender || "",
      phone_number: userDetails?.mobileNumber,
      firstname: kycDetails?.firstname || "",
      lastname: kycDetails?.lastname || "",
      dob: kycDetails?.date_of_birth || "",
      company_gstin: kycDetails?.company_gstin || null,
      pancard_no: kycDetails?.pancard_no || null,
      company_name: kycDetails?.company_name || null,
      operational_address: operational,
    });
    setShowPreview(true);
  };

  if (showPreview) {
    return (
      <UserDetailsPreview
        data={previewData}
        onOnboard={() => submitOnboard(operational)}
        onCancel={() =>
          requestBackWith
            ? requestBackWith(() => backToRole(dispatch))
            : (onRequestCancel || (() => setShowPreview(false)))()
        }
        submitting={submitting}
      />
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: { xs: 4, md: 8 },
        width: "100%",
      }}>
      <Box
        sx={{
          width: { xs: "100%", md: "70vw" },
          maxWidth: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}>
        {isVcp && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              width: "100%",
            }}>
            <Typography variant="h2" sx={{ color: "#333333" }}>
              Business Information
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                flexDirection: { xs: "column", sm: "row" },
              }}>
              {kycDetails?.company_gstin && (
                <TextField
                  label="GST"
                  fullWidth
                  value={kycDetails?.company_gstin}
                  disabled
                />
              )}
              <TextField
                label="PAN"
                fullWidth
                value={kycDetails?.pancard_no || "-"}
                disabled
              />
              <TextField
                label="Firm Name"
                fullWidth
                value={kycDetails?.company_name || "-"}
                disabled
              />
            </Box>
          </Box>
        )}

        <AddressForm
          title={roleName ? `${roleName} Address` : "User Address"}
          value={operational}
          onChange={setOperational}
          errors={errors}
          disableState={
            !!(
              kycDetails?.company_gstin &&
              kycDetails?.operational_address?.state_id
            )
          }
        />
      </Box>

      <CustomButton
        label="Continue"
        fullWidth
        onClick={handleAddressContinue}
        loading={submitting}
        sx={{ width: "616px", maxWidth: "100%" }}
      />
    </Box>
  );
};

export default UserAddressStep;
